<template>
  <div class="sidebar-investigation-list">
    <!-- Search Container (matches Mezmo pipeline search styling) -->
    <div class="p-2 buttons-and-search">
      <div class="d-flex align-items-center">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          placeholder="Find an Investigation"
          autocomplete="off"
          class="form-control form-control-md nav-search-input investigation-search-input"
          aria-label="Find an Investigation"
          data-testid="investigation-search"
          @keydown.escape="clearSearch"
        />
      </div>
    </div>

    <!-- Results Count -->
    <div v-if="searchQuery" class="results-count" aria-live="polite">
      {{ filteredResults.length }} result{{ filteredResults.length !== 1 ? 's' : '' }}
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="spinner-small"></div>
      <p>Loading investigations...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state" role="alert">
      <p>{{ error }}</p>
    </div>

    <!-- Empty States -->
    <div v-else-if="!searchQuery && filteredResults.length === 0" class="empty-state">
      <p>No investigations yet</p>
    </div>

    <div v-else-if="searchQuery && filteredResults.length === 0" class="empty-state">
      <p>No matches found</p>
      <button @click="clearSearch" class="btn-link">Clear search</button>
    </div>

    <!-- Investigation Lists -->
    <div v-else class="investigation-lists">
      <!-- Active Investigations -->
      <div v-if="activeInvestigations.length > 0" class="investigation-group">
        <h3 class="group-title">
          Active Investigations
          <span class="group-count">{{ activeInvestigations.length }}</span>
        </h3>
        <div class="investigation-list">
          <InvestigationListItem
            v-for="investigation in activeInvestigations"
            :key="investigation.session_id || investigation.fingerprint"
            :investigation="investigation"
          />
        </div>
      </div>

      <!-- Recent Investigations -->
      <div v-if="recentInvestigations.length > 0" class="investigation-group">
        <h3 class="group-title">
          Recent Investigations
          <span class="group-count">{{ recentInvestigations.length }}</span>
        </h3>
        <div class="investigation-list">
          <InvestigationListItem
            v-for="investigation in recentInvestigations"
            :key="investigation.session_id || investigation.fingerprint"
            :investigation="investigation"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useInvestigations } from '../../composables/useInvestigations.js';
import InvestigationListItem from './InvestigationListItem.vue';

const { investigations, isLoading, error } = useInvestigations();

const searchInput = ref(null);
const searchQuery = ref('');
let searchDebounceTimeout = null;
const debouncedSearchQuery = ref('');

// Debounced search (300ms)
watch(searchQuery, (newValue) => {
  if (searchDebounceTimeout) {
    clearTimeout(searchDebounceTimeout);
  }

  searchDebounceTimeout = setTimeout(() => {
    debouncedSearchQuery.value = newValue;
  }, 300);
});

// Filter investigations based on search query
const filteredInvestigations = computed(() => {
  if (!debouncedSearchQuery.value) {
    return investigations.value;
  }

  const query = debouncedSearchQuery.value.toLowerCase();
  return investigations.value.filter(inv => {
    const sessionId = (inv.session_id || '').toLowerCase();
    const fingerprint = (inv.fingerprint || '').toLowerCase();
    const namespace = (inv.namespace || '').toLowerCase();

    return (
      sessionId.includes(query) ||
      fingerprint.includes(query) ||
      namespace.includes(query)
    );
  });
});

// Separate active and recent investigations
const activeInvestigations = computed(() => {
  return filteredInvestigations.value.filter(inv => inv.status_code === 'active');
});

const recentInvestigations = computed(() => {
  return filteredInvestigations.value
    .filter(inv => ['success', 'error', 'deduped'].includes(inv.status_code))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 10); // Limit to 10 recent investigations to avoid overwhelming the sidebar
});

// Combined filtered results for search count
const filteredResults = computed(() => {
  return [...activeInvestigations.value, ...recentInvestigations.value];
});

// Clear search
function clearSearch() {
  searchQuery.value = '';
  debouncedSearchQuery.value = '';
}

// Focus search input with "/" key
function handleKeydown(event) {
  if (event.key === '/' && !isInputFocused()) {
    event.preventDefault();
    searchInput.value?.focus();
  }
}

function isInputFocused() {
  const activeElement = document.activeElement;
  return (
    activeElement &&
    (activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable)
  );
}

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
  if (searchDebounceTimeout) {
    clearTimeout(searchDebounceTimeout);
  }
});
</script>

<style scoped>
.sidebar-investigation-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* Search Container (matches Mezmo pipeline search) */
.buttons-and-search {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-search-input {
  width: 100%;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--bs-light);
  font-size: 0.875rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    background: rgba(0, 0, 0, 0.4);
    border-color: var(--bs-primary);
    color: var(--bs-light);
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
  }
}

.investigation-search-input {
  /* Additional investigation-specific styles if needed */
}

/* Results Count */
.results-count {
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  color: var(--bs-secondary);
  border-bottom: 1px solid var(--bs-border-color);
}

/* Investigation Lists */
.investigation-lists {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
}

.investigation-group {
  margin-bottom: 1rem;
}

.group-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #9ca3af !important;
  letter-spacing: 0.5px;
  margin: 0;
}

.group-count {
  background: rgba(255, 255, 255, 0.1);
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 700;
}

.investigation-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 0.5rem;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
  color: var(--bs-secondary);
}

.spinner-small {
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: var(--bs-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Error State */
.error-state {
  padding: 2rem 1rem;
  text-align: center;
  color: var(--bs-danger);
}

/* Empty State */
.empty-state {
  padding: 2rem 1rem;
  text-align: center;
  color: var(--bs-secondary);
}

.empty-state p {
  margin-bottom: 1rem;
}

.btn-link {
  background: none;
  border: none;
  color: var(--bs-primary);
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0;
}

.btn-link:hover {
  color: var(--bs-info);
}

/* Scrollbar styling */
.investigation-lists::-webkit-scrollbar {
  width: 6px;
}

.investigation-lists::-webkit-scrollbar-track {
  background: transparent;
}

.investigation-lists::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.investigation-lists::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
